import { useEffect, useState } from 'react';

interface FetchState {
    data: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    loading: null | boolean;
}

interface FetchOptions {
    method?: 'GET' | 'POST';
    headers?: Record<string, string>;
}

export const useFetch = (url: string, options: FetchOptions = {}, deps?: any[]): any => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const [data, setData] = useState<FetchState>({ data: null, error: null, loading: null });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const headers: Record<string, any> = { 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) };

    const get = <T = any>(query?: any): Promise<T> => { // eslint-disable-line @typescript-eslint/no-explicit-any
        let querySeparator = '?';
        let searchQuery = '';

        if (url.indexOf('?') >= 0) {
            querySeparator = '&';
        }

        if (query) {
            searchQuery = `${querySeparator}${Object.keys(query).map(key => `${key}=${encodeURIComponent(query[key])}`).join('&')}`;
        }

        setData(data => ({ ...data, loading: true }));

        return new Promise((success, failure) => {
            window.fetch(url + searchQuery, {
                method: 'GET',
                headers
            })
                .then(res => {
                    if (!res.ok) {
                        failure(new TypeError(`Error status: ${res.status}`));
                    }

                    return res.json();
                })
                .then(res => {
                    success(res);
                    setData(() => ({ data: res, error: null, loading: false }));
                })
                .catch(err => {
                    failure(err);
                    setData(() => ({ data: null, error: err, loading: false }));
                });
        });
    };

    const post = <T>(data?: any): Promise<T> => { // eslint-disable-line @typescript-eslint/no-explicit-any
        let body: any; // eslint-disable-line @typescript-eslint/no-explicit-any

        if (data) {
            if (data instanceof FormData) {
                body = data;
            } else if (typeof data === 'object') {
                body = JSON.stringify(data);
                headers['content-type'] = 'application/json';
            } else if (typeof data === 'string') {
                body = data;
            } else {
                throw new Error('Invalid data format');
            }
        }

        setData(data => ({ ...data, loading: true }));

        return new Promise((success, failure) => {
            window.fetch(url, {
                body,
                method: 'POST',
                headers
            })
                .then(res => res.json())
                .then(res => {
                    success(res);
                    setData(() => ({ data: res, error: null, loading: false }));
                })
                .catch(err => {
                    failure(err);
                    setData(() => ({ data: null, error: err, loading: false }));
                });
        });
    };

    if (deps !== void 0) {
        useEffect(() => {
            (options.method === 'POST' ? post : get)();
        }, [...deps, url]); // eslint-disable-line react-hooks/exhaustive-deps
    }

    return {
        get,
        post,
        data: data.data,
        error: data.error,
        loading: data.loading
    };
};
